import type {
    LocationDailyLiceReport,
    LocationRangeLiceReport,
    RegionDailyLiceReport,
    RegionRangeLiceReport,
} from '@/domain/liceReport/types';
import { RequestError } from '@/domain/shared/errors';
import { diffDates, toDate } from '@/utils/date';

/** Must match `ClientController.MAX_LICE_REPORT_RANGE_DAYS` on the backend. */
export const MAX_LICE_REPORT_RANGE_DAYS = 366;

export function getInclusiveDayCount(from: string, to: string): number {
    const fromDate = toDate(from);
    const toDateValue = toDate(to);
    if (!fromDate || !toDateValue) {
        return 0;
    }
    return diffDates(toDateValue, fromDate, 'day') + 1;
}

export function isLiceReportRangeWithinLimit(from: string, to: string): boolean {
    const dayCount = getInclusiveDayCount(from, to);
    return dayCount > 0 && dayCount <= MAX_LICE_REPORT_RANGE_DAYS;
}

export function hasLocationDailyReportContent(data: LocationDailyLiceReport): boolean {
    return data.location.cages.length > 0;
}

export function hasRegionDailyReportContent(data: RegionDailyLiceReport): boolean {
    return data.locations.some((location) => location.cages.length > 0);
}

export function hasLocationRangeReportContent(data: LocationRangeLiceReport): boolean {
    return data.location.cages.length > 0;
}

export function hasRegionRangeReportContent(data: RegionRangeLiceReport): boolean {
    return data.locations.some((location) => location.cages.length > 0);
}

const LICE_REPORT_RANGE_MAX_DAYS_MESSAGE = 'Date range cannot exceed';

export function isLiceReportRangeMaxDaysExceededError(error: unknown): boolean {
    if (!RequestError.isRequestError(error) || error.status !== 400) {
        return false;
    }
    const message =
        typeof error.message === 'string' ? error.message : JSON.stringify(error.message);
    return message.includes(LICE_REPORT_RANGE_MAX_DAYS_MESSAGE);
}
